import { type ParsedAsset, type UserProfileDataType } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import Animated, { FadeInUp } from 'react-native-reanimated'
import { PropertyListItem } from '~/components/assets/PropertyListItem'
import { OtpVerification } from '~/components/auth/OtpVerification'
import FlowNavigation from '~/components/FlowNavigation'
import { useAlert } from '~/components/GlobalAlert'
import ContentBox from '~/components/layout/ContentBox'
import { LoadingView } from '~/components/LoadingView'
import { WhatIsAikoaModal } from '~/components/questionnaires/WhatIsAikoa'
import RealtorCard from '~/components/realtors/RealtorCard'
import { ScrollView } from '~/components/ScrollView'
import { Body } from '~/components/typography/Content'
import { Heading1, Heading2 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button } from '~/components/ui/button'
import { CheckboxButton } from '~/components/ui/checkbox'
import { useSession } from '~/components/user/SessionProvider'
import { useSetupBuyerAccount } from '~/data/user/useUserProfile'
import { usePostHog } from '~/lib/analytics/posthog'
import { useResendOtp, useSendOtp, useVerifyOtp } from '~/lib/auth/useOtpAuth'
import { useOtpStateManager } from '~/lib/auth/useOtpStateManager'
import { trpc } from '~/lib/trpc'

type ContactRequestData = {
  id: string
  short_id: string
  status: 'pending' | 'accepted' | 'rejected'
  realtor_user_id: string
  realtor_name: string
  asset: ParsedAsset
  contact: {
    id: string
    name: string
    email: string | null
    phone: string | null
    language: string
  }
}

type ResponseOption = 'accepted' | 'rejected' | 'not-buying'

type ContactRequestResponseFlowProps = {
  contactRequest: ContactRequestData
  onComplete: (status: 'accepted' | 'rejected' | 'not-buying') => void
}

export function ContactRequestResponseFlow({
  contactRequest,
  onComplete,
}: ContactRequestResponseFlowProps) {
  const { t } = useTranslations(['contactRequest', 'feedback'])
  const { session, isAnonymous } = useSession()
  const hasLinkedAuth = !!session?.user && !isAnonymous

  const { showAlert } = useAlert()
  const posthog = usePostHog()

  const [selectedResponse, setSelectedResponse] = useState<ResponseOption | null>(null)

  const [showOtpPhase, setShowOtpPhase] = useState(false)
  const [otp, _setOtp] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current)
      }
    },
    []
  )

  const otpStateManager = useOtpStateManager()
  const ensureBuyerSetup = useSetupBuyerAccount()
  const verifyOtpMutation = useVerifyOtp()
  const sendOtpMutation = useSendOtp({ allowAccountCreation: true })
  const resendOtpMutation = useResendOtp()

  const respondMutation = useMutation(trpc.contacts.respondToContactRequest.mutationOptions())

  const phoneNumber = contactRequest.contact.phone ?? ''

  const submitResponse = useCallback(
    async (response: 'accepted' | 'rejected' | 'not-buying') => {
      try {
        const result = await respondMutation.mutateAsync({
          contactRequestId: contactRequest.id,
          response,
        })

        if (result.success) {
          onComplete(response)
        } else {
          showAlert({
            type: 'error',
            title: t(($) => $.errors.title),
            description: t(($) => $.errors.responseSendFailed),
          })
        }
      } catch {
        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description: t(($) => $.errors.responseSendFailed),
        })
      }
    },
    [contactRequest.id, onComplete, respondMutation, showAlert, t]
  )

  const sendOtp = useCallback(async () => {
    if (!phoneNumber) {
      return false
    }

    if (otpStateManager.hasRecentOtp(phoneNumber)) {
      return true
    }

    try {
      await sendOtpMutation.mutateAsync({ phone: phoneNumber })
      otpStateManager.markOtpSent(phoneNumber)
      return true
    } catch {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.title),
        description: t(($) => $.errors.codeSendFailed),
      })
      return false
    }
  }, [phoneNumber, otpStateManager, sendOtpMutation, showAlert, t])

  const handleVerifyOtp = useCallback(async () => {
    if (!phoneNumber || otp.length !== 6 || !selectedResponse) {
      return
    }

    setIsSubmitting(true)

    try {
      const user = await verifyOtpMutation.mutateAsync({
        phone: phoneNumber,
        token: otp,
        type: 'sms',
      })

      if (!user) {
        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description: t(($) => $.errors.codeVerifyFailed),
        })
        setIsSubmitting(false)
        return
      }

      const userProfileData = {
        name: contactRequest.contact.name,
        avatar_url: user.user_metadata.avatar_url || '',
      } satisfies UserProfileDataType

      await ensureBuyerSetup.mutateAsync({
        profileData: userProfileData,
      })

      posthog.identify(user.id, {
        email: user.email || contactRequest.contact.email || user.user_metadata.email || null,
        name: userProfileData.name ?? '(Anonymous)',
        phoneNumber:
          user.phone || contactRequest.contact.phone || user.user_metadata.phone_number || null,
        role: 'buyer',
      })

      await submitResponse(selectedResponse)
    } catch {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.title),
        description: t(($) => $.errors.verifyFailed),
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    phoneNumber,
    otp,
    selectedResponse,
    verifyOtpMutation,
    contactRequest.contact,
    ensureBuyerSetup,
    posthog,
    submitResponse,
    showAlert,
    t,
  ])

  const handleVerifyOtpRef = useRef(handleVerifyOtp)
  handleVerifyOtpRef.current = handleVerifyOtp

  const setOtp = useCallback((otpValue: string) => {
    _setOtp(otpValue)

    if (otpValue.length !== 6) {
      return
    }

    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current)
    }

    autoSubmitTimeoutRef.current = setTimeout(() => {
      void handleVerifyOtpRef.current()
    }, 500)
  }, [])

  const handleResendOtp = useCallback(async () => {
    if (!phoneNumber || !otpStateManager.canResendOtp(phoneNumber)) {
      return
    }

    try {
      await resendOtpMutation.mutateAsync({
        phone: phoneNumber,
        type: 'sms',
      })
      otpStateManager.markOtpSent(phoneNumber)
    } catch {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.title),
        description: t(($) => $.errors.codeSendFailed),
      })
    }
  }, [phoneNumber, otpStateManager, resendOtpMutation, showAlert, t])

  const handleSelectResponse = useCallback((response: ResponseOption) => {
    setSelectedResponse(response)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!selectedResponse) {
      return
    }

    if (hasLinkedAuth) {
      setIsSubmitting(true)

      try {
        await submitResponse(selectedResponse)
      } finally {
        setIsSubmitting(false)
      }

      return
    }

    if (!phoneNumber) {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.title),
        description: t(($) => $.errors.phoneNotFound),
      })
      return
    }

    const success = await sendOtp()

    if (success) {
      setShowOtpPhase(true)
    }
  }, [selectedResponse, hasLinkedAuth, phoneNumber, submitResponse, sendOtp, showAlert, t])

  const isLoading =
    ensureBuyerSetup.isPending ||
    verifyOtpMutation.isPending ||
    sendOtpMutation.isPending ||
    respondMutation.isPending ||
    isSubmitting

  const canSubmit = selectedResponse !== null
  const isVerifying = verifyOtpMutation.isPending || ensureBuyerSetup.isPending || isSubmitting

  return (
    <>
      <ContentBox
        contentContainerClassName="gap-4 px-6 pb-0 pt-8"
        footerClassName="py-0"
        footer={
          <FlowNavigation
            onNext={handleSubmit}
            nextLabel={t(($) => $.submit)}
            nextDisabled={isLoading || !canSubmit}
          />
        }>
        <View className="gap-4">
          <Heading2 className="text-center font-bold text-2xl">{t(($) => $.title)}</Heading2>

          <Body className="text-center text-base/normal">
            {t(($) => $.description, {
              realtorName: contactRequest.realtor_name,
            })}
          </Body>
          <WhatIsAikoaModal />
        </View>

        <View className="-mx-2">
          <PropertyListItem
            asset={contactRequest.asset}
            showPropertyDetails
            isPressable={false}
            showTypeBadge={false}
          />
        </View>

        <View className="gap-4">
          <CheckboxButton
            checked={selectedResponse === 'accepted'}
            // eslint-disable-next-line i18next/no-literal-string
            onCheckedChange={() => handleSelectResponse('accepted')}
            disabled={isLoading}
            label={t(($) => $.options.accept.title)}
            description={t(($) => $.options.accept.description)}
          />

          <CheckboxButton
            checked={selectedResponse === 'rejected'}
            // eslint-disable-next-line i18next/no-literal-string
            onCheckedChange={() => handleSelectResponse('rejected')}
            disabled={isLoading}
            label={t(($) => $.options.reject.title)}
            description={t(($) => $.options.reject.description)}
          />

          <CheckboxButton
            checked={selectedResponse === 'not-buying'}
            onCheckedChange={() => handleSelectResponse('not-buying')}
            disabled={isLoading}
            label={t(($) => $.options.notBuying.title)}
            description={t(($) => $.options.notBuying.description)}
          />
        </View>

        <View className="gap-4">
          <Body className="pb-4 text-center text-sm text-foreground/90">
            {t(($) => $.disclaimer)}
          </Body>

          {contactRequest.realtor_user_id && (
            <RealtorCard
              userId={contactRequest.realtor_user_id}
              size="default"
              className="mx-auto mb-8"
            />
          )}
        </View>
      </ContentBox>

      <BottomSheet
        modalKey="contact-request-otp"
        isOpen={showOtpPhase}
        showHandle={false}
        showCloseButton={false}>
        <ScrollView
          className="max-h-[85vh] min-h-[350px] flex-1"
          contentContainerClassName="gap-6 px-6 pb-6 pt-8"
          footerClassName="py-0 bottom-6!"
          footer={
            <Button
              variant="ghost"
              size="lg"
              onPress={() => {
                setShowOtpPhase(false)
                _setOtp('')
              }}
              disabled={isVerifying}>
              {t(($) => $.otp.back)}
            </Button>
          }>
          <View className="gap-3">
            <Heading1 className="text-center">{t(($) => $.otp.title)}</Heading1>
            <Body className="px-6 text-center">{t(($) => $.otp.subtitle)}</Body>
          </View>

          {phoneNumber && (
            <Animated.View
              className="w-full items-center gap-4 rounded-2xl border border-tertiary-foreground/90 bg-tertiary-foreground/40 px-5 py-6"
              entering={FadeInUp.duration(150)}>
              <View className="gap-2">
                <Body className="text-center text-sm">{t(($) => $.otp.codeSent)}</Body>
                <Body className="text-center font-medium">{phoneNumber}</Body>
              </View>

              <OtpVerification
                otp={otp}
                setOtp={setOtp}
                disabled={isVerifying}
                key={`otp-phone-${phoneNumber}`}
                onResend={handleResendOtp}
                otpStateManager={otpStateManager}
                pendingChange={phoneNumber}
              />

              {isVerifying && (
                <Animated.View className="items-center gap-2" entering={FadeInUp.duration(150)}>
                  <LoadingView className="flex-none" />
                  <Body className="text-center text-sm text-foreground/70">
                    {t(($) => $.otp.verifying)}
                  </Body>
                </Animated.View>
              )}
            </Animated.View>
          )}
        </ScrollView>
      </BottomSheet>
    </>
  )
}
